import { Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Card, CardContent } from '~/components/ui/card';
import { Skeleton } from '~/components/ui/skeleton';
import { cn } from '~/utils/cn';
import { downloadCsv } from '~/utils/csv';

import { useExperimentAdjustedMetrics } from '../../hooks/use-experiments';
import type { AdjustedMetricsRow } from '../../types/experiment.types';

import { ExperimentDataUnavailable } from './experiment-data-unavailable';
import { RevenueSourceTag } from './revenue-source-tag';

interface Props {
  experimentId: string;
}

const COLUMNS = [
  { key: 'arm', labelKey: 'detail.health.adjustedMetrics.columns.arms' },
  { key: 'duration', labelKey: 'detail.health.adjustedMetrics.columns.duration' },
  { key: 'parentIds', labelKey: 'detail.health.adjustedMetrics.columns.parentIds' },
  { key: 'childIds', labelKey: 'detail.health.adjustedMetrics.columns.childIds' },
  { key: 'prePeriodRevenue', labelKey: 'detail.health.adjustedMetrics.columns.prePeriodRevenue' },
  { key: 'postPeriodRevenue', labelKey: 'detail.health.adjustedMetrics.columns.postPeriodRevenue' },
] as const;

// Pins the "Test" arm as the first row so merchants always see the treatment
// arm before Control, regardless of the order the API returns rows in.
function sortTestArmFirst(rows: AdjustedMetricsRow[]): AdjustedMetricsRow[] {
  return [...rows].sort((a, b) => Number(b.arm === 'Test') - Number(a.arm === 'Test'));
}

export function ExperimentAdjustedMetrics({ experimentId }: Props) {
  const { t } = useTranslation('experiments');
  const { data, isLoading, isError } = useExperimentAdjustedMetrics(experimentId);
  const rows = data ? sortTestArmFirst(data.rows) : [];

  // Exports the sorted rows with the same number formatting shown in the table cells.
  const handleExport = () => {
    if (rows.length === 0) return;
    const csvRows = rows.map((row) => ({
      arm: row.arm,
      duration: row.duration,
      parentIds: row.parentIds.toLocaleString('en-US'),
      childIds: row.childIds.toLocaleString('en-US'),
      prePeriodRevenue: row.prePeriodRevenue,
      postPeriodRevenue: row.postPeriodRevenue,
    }));
    downloadCsv(
      'adjusted-metrics',
      csvRows,
      COLUMNS.map((col) => ({ key: col.key, label: t(col.labelKey) })),
    );
  };

  return (
    <Card className="min-w-0 overflow-hidden">
      <CardContent className="min-w-0 space-y-6 p-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 flex-col">
            <h3 className="flex flex-wrap items-center gap-2 text-[16px] font-medium wrap-break-word">
              {t('detail.health.adjustedMetrics.title')}
              <RevenueSourceTag source={data?.revenueSource} />
            </h3>
            <p className="text-muted-foreground text-sm wrap-break-word">
              {t('detail.health.adjustedMetrics.subtitle')}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={isLoading || isError || rows.length === 0}
            className={cn(
              'gap-2',
              isLoading || isError || rows.length === 0 ? 'cursor-not-allowed' : 'cursor-pointer',
            )}
          >
            <Upload className="h-4 w-4" aria-hidden="true" />
            {t('detail.health.adjustedMetrics.exportCsv')}
          </Button>
        </div>

        {/* Table — collapses to a "data unavailable" line when the source
            query throws (no backend endpoint yet). */}
        {isError ? (
          <ExperimentDataUnavailable />
        ) : (
          <>
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full min-w-[720px] text-sm">
                <thead className="bg-muted/30">
                  <tr>
                    {COLUMNS.map((col) => (
                      <th
                        key={col.key}
                        className="text-muted-foreground px-4 py-3 text-left font-medium"
                      >
                        {t(col.labelKey)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {isLoading || !data
                    ? Array.from({ length: 2 }).map((_, i) => (
                        <tr key={i} className="border-t">
                          {COLUMNS.map((col) => (
                            <td key={col.key} className="px-4 py-4">
                              <Skeleton className="h-4 w-20" />
                            </td>
                          ))}
                        </tr>
                      ))
                    : rows.map((row) => (
                        <tr key={row.arm} className="border-t">
                          <td className="px-4 py-4 font-medium">{row.arm}</td>
                          <td className="text-muted-foreground px-4 py-4">{row.duration}</td>
                          <td className="text-muted-foreground px-4 py-4">
                            {row.parentIds.toLocaleString('en-US')}
                          </td>
                          <td className="text-muted-foreground px-4 py-4">
                            {row.childIds.toLocaleString('en-US')}
                          </td>
                          <td className="text-muted-foreground px-4 py-4">
                            {row.prePeriodRevenue}
                          </td>
                          <td className="text-muted-foreground px-4 py-4">
                            {row.postPeriodRevenue}
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>

            {/* Callouts */}
            {isLoading || !data ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {data.callouts.map((c) => (
                  <div key={c.title} className="bg-muted/30 rounded-md border p-4">
                    <p className="text-foreground font-semibold">{c.title}</p>
                    <p className="text-muted-foreground mt-1 text-sm">{c.body}</p>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
